const MIN_AGE = 18;

const CONTROL_FLOW = [
  'age',
  'on-glp1',
  'conditions',
  'state',
  'name',
  'email',
  'phone',
  'congrats',
  'select',
  'pricing',
];

const ENHANCED_FLOW = [
  'goal',
  'root-cause',
  'age',
  'on-glp1',
  'science',
  'conditions',
  'state',
  'timeline',
  'name',
  'email',
  'phone',
  'congrats',
  'select',
  'pricing',
];

const LEGACY_STEP_KEYS = {
  1: 'age',
  2: 'on-glp1',
  3: 'conditions',
  4: 'state',
  5: 'name',
  6: 'email',
  7: 'phone',
  8: 'congrats',
  9: 'pricing',
};

const LEGACY_KEY_STEPS = Object.fromEntries(
  Object.entries(LEGACY_STEP_KEYS).map(([step, key]) => [key, step]),
);

class TaIntake extends HTMLElement {
  connectedCallback() {
    this.answers = {};
    this.screens = Array.from(this.querySelectorAll('[data-screen]'));
    this.progress = this.querySelector('[data-progress]');
    this.barFill = this.querySelector('[data-bar-fill]');
    this.currentEl = this.querySelector('[data-current]');
    this.totalEl = this.querySelector('[data-total]');

    this.ready = false;
    this.products = this.readProducts();
    this.treatment = this.treatmentFromUrl();
    this.forcedTreatment = Boolean(this.treatment);
    if (this.treatment) this.answers.treatment = this.productName(this.treatment);

    const experienceParam = new URLSearchParams(window.location.search).get('experience');
    this.enhanced =
      experienceParam === 'enhanced' ||
      (experienceParam !== 'control' && this.dataset.enhancedFlow === 'true');
    this.experience = this.enhanced ? 'enhanced' : 'control';
    this.flow = (this.enhanced ? ENHANCED_FLOW : CONTROL_FLOW).filter(
      (key) => key !== 'select' || !this.forcedTreatment,
    );
    this.questionKeys = this.flow.filter((key) => this.getScreen(key)?.dataset.kind === 'question');
    if (this.totalEl) this.totalEl.textContent = String(this.questionKeys.length);

    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
    this.setupConditions();

    window.addEventListener('popstate', () => this.render(this.keyFromUrl(), { push: false }));
    this.render(this.keyFromUrl(), { push: false, replace: true });
    this.revealWhenReady();
  }

  revealWhenReady() {
    const loader = this.querySelector('[data-loader]');

    const finish = () => {
      this.ready = true;

      const intro = this.getScreen('intro');
      if (intro && !intro.hidden) this.typeIntro(intro);

      this.classList.add('is-ready');

      if (loader) {
        const remove = () => {
          loader.hidden = true;
          loader.removeEventListener('transitionend', remove);
        };
        loader.addEventListener('transitionend', remove);
        setTimeout(remove, 600);
      }
    };

    const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    const timeout = new Promise((resolve) => setTimeout(resolve, 1200));
    Promise.race([fontsReady, timeout]).then(() => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(finish));
    });
  }

  readProducts() {
    const el = this.querySelector('[data-products]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  treatmentFromUrl() {
    const value = (new URLSearchParams(window.location.search).get('treatment') || '')
      .toLowerCase()
      .trim();
    const product = this.products[value];
    return product && product.variantId ? value : null;
  }

  productName(slug) {
    return this.products[slug]?.name || '';
  }

  /* ---- navigation ---- */

  onClick = (event) => {
    const trigger = event.target.closest('[data-action]');
    if (!trigger || !this.contains(trigger)) return;
    const action = trigger.dataset.action;
    if (action === 'start') {
      event.preventDefault();
      this.go(this.flow[0]);
    } else if (action === 'next') {
      event.preventDefault();
      this.handleNext();
    } else if (action === 'back') {
      event.preventDefault();
      this.handleBack();
    } else if (action === 'checkout') {
      event.preventDefault();
      this.handleCheckout(trigger);
    }
  };

  onSubmit = (event) => {
    event.preventDefault();
    this.handleNext();
  };

  handleNext() {
    const key = this.currentKey;

    if (key === 'select') {
      const selectScreen = this.getScreen('select');
      const choice = selectScreen.querySelector('[name="treatment"]:checked');
      if (!choice) {
        this.showError(selectScreen, 'Please choose a treatment to continue.');
        return;
      }
      this.clearError(selectScreen);
      this.treatment = choice.value;
      this.answers.treatment = this.productName(this.treatment) || choice.value;
      this.track('treatment_selection', { treatment: this.treatment });
      this.go(this.nextKey(key));
      return;
    }

    const screen = this.getScreen(key);
    const result = this.validateStep(key, screen);
    if (!result.valid) {
      this.showError(screen, result.error);
      return;
    }
    this.clearError(screen);
    this.trackAnswer(screen);

    if (result.disqualify) {
      this.track('disqualification', { reason: 'eligibility' });
      this.go('disqualified');
      return;
    }

    if (key === 'phone') this.track('contact_complete');
    this.go(this.nextKey(key));
  }

  handleBack() {
    const key = this.currentKey;
    if (key === 'disqualified') {
      this.go('intro');
      return;
    }
    const previous = this.previousKey(key);
    this.go(previous || 'intro');
  }

  go(key) {
    if (this.animating || !key) return;
    this.render(key, { push: true });
  }

  nextKey(key) {
    const index = this.flow.indexOf(key);
    return index >= 0 ? this.flow[index + 1] : null;
  }

  previousKey(key) {
    const index = this.flow.indexOf(key);
    return index > 0 ? this.flow[index - 1] : null;
  }

  /* ---- rendering ---- */

  orderOf(key) {
    if (key === 'intro') return 0;
    if (key === 'disqualified') return 99;
    const index = this.flow.indexOf(key);
    return index < 0 ? 0 : index + 1;
  }

  render(key, { push = true, replace = false, animate } = {}) {
    const target = this.getScreen(key);
    if (!target) {
      this.render('intro', { push, replace });
      return;
    }
    if (animate === undefined) animate = !replace;

    const prev = this.activeScreen;
    const back = prev ? this.orderOf(key) < this.orderOf(this.currentKey) : false;

    this.currentKey = key;
    this.activeScreen = target;
    this.syncHistory(key, { push, replace });

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!animate || reduceMotion || !prev || prev === target) {
      this.updateChrome(key);
      this.screens.forEach((screen) => {
        screen.hidden = screen !== target;
        screen.classList.remove('is-entering', 'is-leaving', 'is-back');
      });
      this.afterShow(target, key);
      return;
    }

    this.animating = true;
    prev.classList.remove('is-entering');
    prev.classList.toggle('is-back', back);
    prev.classList.add('is-leaving');

    const onLeave = () => {
      prev.removeEventListener('animationend', onLeave);
      this.screens.forEach((screen) => {
        if (screen !== target) screen.hidden = true;
      });
      prev.classList.remove('is-leaving', 'is-back');

      // Update chrome (centering + progress bar) only after the previous
      // screen has left, so its exit doesn't visibly jump.
      this.updateChrome(key);

      target.hidden = false;
      void target.offsetWidth; // force reflow so the entering animation restarts
      target.classList.toggle('is-back', back);
      target.classList.add('is-entering');

      const onEnter = () => {
        target.removeEventListener('animationend', onEnter);
        target.classList.remove('is-entering', 'is-back');
        this.animating = false;
        this.afterShow(target, key);
      };
      target.addEventListener('animationend', onEnter);
    };
    prev.addEventListener('animationend', onLeave);
  }

  updateChrome(key) {
    const screen = this.getScreen(key);
    const showProgress = screen?.dataset.kind === 'question' || screen?.dataset.kind === 'interstitial';
    if (this.progress) this.progress.hidden = !showProgress;
    if (showProgress) {
      const flowIndex = this.flow.indexOf(key);
      const questionsReached = this.flow
        .slice(0, flowIndex + 1)
        .filter((flowKey) => this.getScreen(flowKey)?.dataset.kind === 'question').length;
      const current = Math.max(questionsReached, 1);
      const total = Math.max(this.questionKeys.length, 1);
      if (this.barFill) this.barFill.style.width = `${(current / total) * 100}%`;
      if (this.currentEl) this.currentEl.textContent = String(current);
    }
    const centered = key === 'intro' || key === 'disqualified';
    const viewport = this.querySelector('.ta-intake__viewport');
    if (viewport) viewport.classList.toggle('is-center', centered);
    const globalBack = this.querySelector('[data-global-back]');
    if (globalBack) globalBack.hidden = centered;
  }

  afterShow(target, key) {
    if (key === 'intro' && this.ready) this.typeIntro(target);
    if (key === 'pricing') {
      if (!this.treatment) {
        this.render(this.forcedTreatment ? 'congrats' : 'select', { replace: true });
        return;
      }
      this.populatePlan();
    }
    if (key === 'congrats') this.personalizeCongrats();
    const focusable = target.querySelector('input:not([type=checkbox]):not([type=radio]), select');
    if (focusable && target.dataset.kind === 'question') {
      window.requestAnimationFrame(() => focusable.focus({ preventScroll: false }));
    }
    target.scrollIntoView({ block: 'nearest' });
    this.track('screen_view', {
      screen: key,
      kind: target.dataset.kind || 'unknown',
    });
  }

  populatePlan() {
    const data = this.products[this.treatment];
    const screen = this.getScreen('pricing');
    if (!data || !screen) return;
    const plan = screen.querySelector('[data-plan]');
    const nameEl = screen.querySelector('[data-plan-name]');
    const nowEl = screen.querySelector('[data-plan-now]');
    const wasEl = screen.querySelector('[data-plan-was]');
    if (nameEl) nameEl.textContent = data.name || '';
    if (nowEl) nowEl.textContent = data.price || '';
    if (wasEl) {
      if (data.compareAt) {
        wasEl.textContent = data.compareAt;
        wasEl.hidden = false;
      } else {
        wasEl.hidden = true;
      }
    }
    if (plan) plan.hidden = false;
  }

  personalizeCongrats() {
    const heading = this.querySelector('[data-congrats-heading]');
    if (!heading) return;
    const base = this.dataset.congratsBase || 'Congratulations!';
    const prefix = this.dataset.congratsPrefix || 'Your next step toward';
    heading.textContent = this.answers.goalLabel
      ? `${prefix} ${this.answers.goalLabel.toLowerCase()}.`
      : base;
  }

  trackAnswer(screen) {
    const field = screen?.dataset.field;
    if (!field) return;
    const checked = screen.querySelectorAll('input:checked').length;
    this.track('answer', {
      screen: this.currentKey,
      field,
      selection_count: Math.max(checked, 1),
    });
  }

  track(name, detail = {}) {
    const eventName = `ta_intake_${name}`;
    const payload = {
      event: eventName,
      intake_experience: this.experience,
      ...detail,
    };

    this.dispatchEvent(
      new CustomEvent(`ta:intake:${name.replaceAll('_', '-')}`, {
        bubbles: true,
        detail: payload,
      }),
    );

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(payload);
    }
  }

  typeIntro(target) {
    const title = target.querySelector('[data-typewriter]');
    if (!title || title.dataset.typed) return;
    title.dataset.typed = '1';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const CHAR_STEP = 0.04; // delay between each letter
    const CHAR_DUR = 0.55; // matches the CSS char animation duration
    const LINE_PAUSE = 0.5; // beat after a line finishes before the next begins

    const lines = Array.from(title.querySelectorAll('.ta-intake__title-line'));
    let delay = 0.18;
    let lastCharDelay = delay;

    lines.forEach((line, lineIndex) => {
      if (lineIndex > 0) {
        delay = lastCharDelay + CHAR_DUR + LINE_PAUSE;
      }
      const text = line.textContent;
      line.textContent = '';
      line.setAttribute('aria-label', text);
      const words = text.split(' ');
      words.forEach((word, wordIndex) => {
        const wordEl = document.createElement('span');
        wordEl.className = 'ta-intake__word';
        wordEl.setAttribute('aria-hidden', 'true');
        for (const char of word) {
          const span = document.createElement('span');
          span.className = 'ta-intake__char';
          span.textContent = char;
          span.style.animationDelay = `${delay.toFixed(3)}s`;
          lastCharDelay = delay;
          delay += CHAR_STEP;
          wordEl.appendChild(span);
        }
        line.appendChild(wordEl);
        if (wordIndex < words.length - 1) {
          line.appendChild(document.createTextNode(' '));
          delay += CHAR_STEP;
        }
      });
    });

    // The subtext and button follow once the heading has fully typed out.
    const finish = lastCharDelay + CHAR_DUR;
    const sub = target.querySelector('.ta-intake__sub');
    const button = target.querySelector('.ta-intake__btn');
    if (sub) sub.style.animationDelay = `${(finish + 0.1).toFixed(3)}s`;
    if (button) button.style.animationDelay = `${(finish + 0.28).toFixed(3)}s`;
  }

  getScreen(key) {
    return this.screens?.find((screen) => screen.dataset.key === String(key)) || null;
  }

  /* ---- url state ---- */

  keyFromUrl() {
    const value = new URLSearchParams(window.location.search).get('step');
    if (!value || value === 'start') return 'intro';
    if (value === 'unavailable') return 'disqualified';
    const num = Number(value);
    const key = Number.isInteger(num) && LEGACY_STEP_KEYS[num] ? LEGACY_STEP_KEYS[num] : value;
    if (this.flow.includes(key)) return key;
    if (key === 'select' && this.forcedTreatment) return 'congrats';
    return 'intro';
  }

  syncHistory(key, { push, replace }) {
    const params = new URLSearchParams(window.location.search);
    let value = 'start';
    if (key === 'disqualified') value = 'unavailable';
    else if (LEGACY_KEY_STEPS[key]) value = String(LEGACY_KEY_STEPS[key]);
    else if (key !== 'intro') value = key;

    if (value === 'start') params.delete('step');
    else params.set('step', value);

    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
    if (replace) window.history.replaceState({ step: value }, '', url);
    else if (push) window.history.pushState({ step: value }, '', url);
  }

  /* ---- validation ---- */

  validateStep(key, screen) {
    const field = screen?.dataset.field;

    if (field === 'goal') {
      const choice = screen.querySelector('[name="goal"]:checked');
      if (!choice) return { valid: false, error: 'Please select an option.' };
      this.answers.goal = choice.value;
      this.answers.goalLabel =
        choice.closest('.ta-intake__option')?.querySelector('.ta-intake__option-label')?.textContent.trim() || '';
      return { valid: true };
    }

    if (field === 'age') {
      const age = Number(screen.querySelector('[name="age"]').value);
      if (!age || age < 1) return { valid: false, error: 'Please enter your age.' };
      this.answers.age = age;
      return { valid: true, disqualify: age < MIN_AGE };
    }

    if (field === 'on_glp1') {
      const choice = screen.querySelector('[name="on_glp1"]:checked');
      if (!choice) return { valid: false, error: 'Please select an option.' };
      this.answers.on_glp1 = choice.value;
      return { valid: true };
    }

    if (field === 'conditions') {
      const exclusions = Array.from(screen.querySelectorAll('[data-exclusion]:checked')).map((el) => el.value);
      const none = screen.querySelector('[data-none]:checked');
      if (exclusions.length === 0 && !none) {
        return { valid: false, error: 'Please make a selection.' };
      }
      this.answers.conditions = exclusions.length ? exclusions.join('; ') : 'None';
      return { valid: true, disqualify: exclusions.length > 0 };
    }

    if (field === 'state') {
      const state = screen.querySelector('[name="state"]').value;
      if (!state) return { valid: false, error: 'Please select your state.' };
      this.answers.state = state;
      return { valid: true };
    }

    if (field === 'name') {
      const first = screen.querySelector('[name="first_name"]').value.trim();
      const last = screen.querySelector('[name="last_name"]').value.trim();
      if (!first || !last) return { valid: false, error: 'Please enter your first and last name.' };
      this.answers.first_name = first;
      this.answers.last_name = last;
      return { valid: true };
    }

    if (field === 'email') {
      const email = screen.querySelector('[name="email"]').value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return { valid: false, error: 'Please enter a valid email address.' };
      }
      this.answers.email = email;
      this.answers.email_consent = screen.querySelector('[name="email_consent"]')?.checked ? 'Yes' : 'No';
      return { valid: true };
    }

    if (field === 'phone') {
      const raw = screen.querySelector('[name="phone"]').value.replace(/\D/g, '');
      if (raw.length < 10) return { valid: false, error: 'Please enter a valid phone number.' };
      this.answers.phone = `+1${raw.slice(-10)}`;
      this.answers.sms_consent = screen.querySelector('[name="sms_consent"]')?.checked ? 'Yes' : 'No';
      return { valid: true };
    }

    return { valid: true };
  }

  showError(screen, message) {
    const el = screen?.querySelector('[data-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  clearError(screen) {
    const el = screen?.querySelector('[data-error]');
    if (el) el.hidden = true;
  }

  /* ---- conditions (exclusive "none") ---- */

  setupConditions() {
    const screen = this.querySelector('[data-field="conditions"]');
    if (!screen) return;
    const nones = Array.from(screen.querySelectorAll('[data-none]'));
    const exclusions = Array.from(screen.querySelectorAll('[data-exclusion]'));

    nones.forEach((none) => {
      none.addEventListener('change', () => {
        if (none.checked) {
          exclusions.forEach((ex) => (ex.checked = false));
          nones.forEach((n) => (n.checked = true));
        } else {
          nones.forEach((n) => (n.checked = false));
        }
      });
    });

    exclusions.forEach((ex) => {
      ex.addEventListener('change', () => {
        if (ex.checked) nones.forEach((n) => (n.checked = false));
      });
    });
  }

  /* ---- checkout ---- */

  async handleCheckout(button) {
    const addUrl = this.appendJs(this.dataset.cartAddUrl);
    const updateUrl = this.appendJs(this.dataset.cartUpdateUrl);
    const checkoutUrl = this.dataset.checkoutUrl || '/checkout';
    const variantId = this.products[this.treatment]?.variantId;
    const screen = this.getScreen('pricing');
    const label = button.querySelector('[data-checkout-label]') || button;
    const originalText = label.textContent;

    if (!variantId) {
      this.showError(screen, 'This treatment is unavailable right now. Please try again later.');
      return;
    }

    button.disabled = true;
    label.textContent = 'Processing…';
    this.clearError(screen);
    this.track('checkout_click', { treatment: this.treatment || 'unknown' });

    try {
      const addRes = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!addRes.ok) throw new Error('add');

      await fetch(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: this.buildAttributes() }),
      });

      window.location.href = checkoutUrl;
    } catch (error) {
      button.disabled = false;
      label.textContent = originalText;
      this.showError(screen, 'Something went wrong. Please try again.');
    }
  }

  appendJs(url) {
    if (!url) return '/cart/add.js';
    return url.endsWith('.js') ? url : `${url}.js`;
  }

  buildAttributes() {
    const a = this.answers;
    return {
      'Intake — Age': a.age != null ? String(a.age) : '',
      'Intake — On weight-loss Rx': a.on_glp1 || '',
      'Intake — Health conditions': a.conditions || '',
      'Intake — State': a.state || '',
      'Intake — First name': a.first_name || '',
      'Intake — Last name': a.last_name || '',
      'Intake — Email': a.email || '',
      'Intake — Email consent': a.email_consent || '',
      'Intake — Phone': a.phone || '',
      'Intake — SMS consent': a.sms_consent || '',
      'Intake — Treatment': a.treatment || '',
    };
  }
}

if (!customElements.get('ta-intake')) {
  customElements.define('ta-intake', TaIntake);
}
